// Crew identity helpers — same FK-first / text-fallback pattern as
// stageMatching, but for "person on this briefing".
//
// Schema (post-migration 20260504240100_briefing_crew_canonical):
//   briefing_crew                — canonical "person on briefing" entity
//     id          UUID PK
//     briefing_id UUID FK → briefings
//     email       TEXT (UNIQUE per briefing_id, lowercased on save)
//     user_id     UUID FK → auth.users (NULL until first login)
//   crew_members                 — per-day per-person variability
//     id                  UUID PK
//     briefing_id         UUID FK → briefings
//     briefing_crew_id    UUID FK → briefing_crew (auto-filled by trigger)
//     email, user_id, event_date, function, …
//
// Why this file exists: ~25 sites across web + mobile compute "is
// this the same person?" by string-comparing emails. Case drift
// (`Foo@x.com` vs `foo@x.com`) and whitespace fragmented one human
// into multiple chips/rows/invitations. The canonical FK is now
// available; this helper encodes the fallback rule once with tests.

/**
 * Anything carrying enough fields to identify a person on a briefing.
 * `briefing_crew_id` is the post-canonical FK; `user_id` is the
 * authenticated-user FK (only set after first login); email is the
 * legacy text key.
 */
export type CrewIdentityRow = {
  briefing_crew_id?: string | null;
  user_id?: string | null;
  email?: string | null;
};

/**
 * Stable, case-insensitive identity key for a crew row.
 *
 * Priority order:
 *   1. `bc:<UUID>` — briefing_crew_id (the canonical FK)
 *   2. `u:<UUID>`  — auth user_id (set after first login)
 *   3. `email:<lowercase trimmed>` — legacy fallback for rows whose
 *      FKs haven't backfilled yet.
 *
 * Same row's identity is stable as long as ONE of the three keys
 * persists, so a producer renaming an email at the same time the
 * trigger fills the FK doesn't fragment the person.
 */
export function crewIdentityKey(row: CrewIdentityRow): string {
  if (row.briefing_crew_id) return `bc:${row.briefing_crew_id}`;
  if (row.user_id) return `u:${row.user_id}`;
  return `email:${(row.email ?? "").toLowerCase().trim()}`;
}

/**
 * Two crew rows refer to the same person on the same briefing.
 *
 * Mirrors `crewIdentityKey` semantics. Used by drag-drop, filter,
 * and dedup paths that don't want to allocate a Set/Map per call.
 */
export function sameCrewIdentity(a: CrewIdentityRow, b: CrewIdentityRow): boolean {
  return crewIdentityKey(a) === crewIdentityKey(b);
}

/**
 * Group a list of per-day `crew_members` rows by the canonical
 * person identity. Returns one entry per logical person with all
 * their per-day rows attached.
 *
 * Iteration order is the order each new identity is first seen
 * (callers that want a different sort can re-sort the result).
 */
export function groupByCrewIdentity<T extends CrewIdentityRow>(
  rows: T[],
): Array<{ key: string; rows: T[] }> {
  const order: string[] = [];
  const buckets = new Map<string, { key: string; rows: T[] }>();
  for (const row of rows) {
    const key = crewIdentityKey(row);
    if (!buckets.has(key)) {
      order.push(key);
      buckets.set(key, { key, rows: [] });
    }
    buckets.get(key)!.rows.push(row);
  }
  return order.map((k) => buckets.get(k)!);
}

/**
 * Filter to one row per person. Keeps the first-seen row of each
 * identity. Most readers want this for chip/list rendering where
 * multi-day rows would otherwise duplicate visually.
 */
export function dedupeByCrewIdentity<T extends CrewIdentityRow>(rows: T[]): T[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = crewIdentityKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
